import { http, tokenStorage, type AuthTokens } from '@shared/api';
import { decodeJwt } from '@shared/lib/jwt';
import type { AdminIdentity } from '@entities/session';

interface LoginResponse {
  tokens: AuthTokens;
  identity: AdminIdentity;
}

interface RawLoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  refreshExpiresIn: number;
  // Some deployments include this; we degrade gracefully when absent.
  user?: {
    id: string;
    email: string;
    displayName: string | null;
  };
}

interface UserBasic {
  id: string;
  email: string | null;
  displayName: string;
}

export async function loginWithPassword(identity: string, password: string): Promise<LoginResponse> {
  const raw = await http
    .post('auth/login', { json: { identity, password } })
    .json<RawLoginResponse>();

  const tokens: AuthTokens = {
    accessToken: raw.accessToken,
    refreshToken: raw.refreshToken,
    expiresIn: raw.expiresIn,
    refreshExpiresIn: raw.refreshExpiresIn,
  };

  // Seed token storage so the subsequent /users/{id}/basic call is authorised.
  tokenStorage.set(tokens);

  if (raw.user) {
    return {
      tokens,
      identity: {
        id: raw.user.id,
        email: raw.user.email,
        displayName: raw.user.displayName,
      },
    };
  }

  const sub = decodeJwt(raw.accessToken)?.sub ?? '';
  let resolved: AdminIdentity = { id: sub, email: identity, displayName: null };

  if (sub) {
    try {
      const basic = await http.get(`users/${sub}/basic`).json<UserBasic>();
      resolved = {
        id: basic.id,
        email: basic.email ?? identity,
        displayName: basic.displayName,
      };
    } catch {
      // Keep JWT-derived fallback identity.
    }
  }

  return { tokens, identity: resolved };
}
